import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { Container, Row, Col, Alert } from "react-bootstrap";

import {
  getItems,
  searchItems,
  searchAdvanceItems,
  getItemByCategory,
} from "../../actions/itemActions";
import {
  filterAll,
  filterCondition,
  filterPrice,
  filterPriceAndCondition,
  filterPriceAndRating,
  filterRating,
  filterRatingAndCondition,
  resetFilter,
} from "../../actions/filterActions";

import Navbar from "../../components/Navbar/Navbar";
import ProductCard from "../../components/ProductBanner/ProductCard/ProductCard";
import FilterBox from "../../components/FilterBox/FilterBox";
import ProductLayout from "../../components/ProductLayout/ProductLayout";
import Footer from "../../components/Footer/Footer";
import Pagination from "../../components/Pagination/Pagination";
import Loader from "./Loader";

import styles from "./product-page.module.css";

const Product = () => {
  const dispatch = useDispatch();
  const { items = [], loading } = useSelector((state) => state.itemReducer);
  const { products = [] } = useSelector((state) => state.filterReducer);

  const [searchParams, setSearchParams] = useSearchParams();
  const category = searchParams.get("category") || "";
  const keyword = searchParams.get("keyword") || "";
  const make = searchParams.get("make") || "";
  const model = searchParams.get("model") || "";
  const year = searchParams.get("year") || "";
  const engineType = searchParams.get("engineType") || "";
  const vehicleType = searchParams.get("vehicleType") || "";
  const page = parseInt(searchParams.get("page"), 10) || 1;
  const filtered = searchParams.get("filtered") === "1";

  const [itemsPerPage] = useState(12);
  const [error, setError] = useState("");

  useEffect(() => {
    if (keyword) {
      dispatch(searchItems(keyword));
    } else if (make || model || year || engineType || vehicleType) {
      dispatch(searchAdvanceItems({ make, model, year, engineType, vehicleType }));
    } else if (category) {
      dispatch(getItemByCategory(`api/product/find/category/${category}/`));
    } else {
      dispatch(getItems("api/product/"));
    }
  }, [dispatch, keyword, category, make, model, year, engineType, vehicleType]);

  const applyFilter = useCallback(
    (minprice, maxprice, rating, condition) => {
      const hasAny = Boolean(minprice || maxprice || rating || condition);
      if (!hasAny) {
        setError("Please apply at least one filter");
        return;
      }
      setError("");
      dispatch(resetFilter());

      if (minprice && maxprice && rating && condition) {
        dispatch(filterAll(minprice, maxprice, rating, condition));
      } else if (minprice && maxprice && rating) {
        dispatch(filterPriceAndRating(minprice, maxprice, rating));
      } else if (minprice && maxprice && condition) {
        dispatch(filterPriceAndCondition(minprice, maxprice, condition));
      } else if (rating && condition) {
        dispatch(filterRatingAndCondition(rating, condition));
      } else if (minprice || maxprice) {
        dispatch(filterPrice(minprice || 0, maxprice || 0));
      } else if (rating) {
        dispatch(filterRating(rating));
      } else if (condition) {
        dispatch(filterCondition(condition));
      }

      setSearchParams({
        ...(category ? { category } : {}),
        ...(keyword ? { keyword } : {}),
        page: 1,
        filtered: "1",
      });
    },
    [dispatch, setSearchParams, category, keyword]
  );

  const onResetFilters = useCallback(() => {
    dispatch(resetFilter());
    setError("");
    setSearchParams({
      ...(category ? { category } : {}),
      ...(keyword ? { keyword } : {}),
      page: 1,
    });
  }, [dispatch, setSearchParams, category, keyword]);

  const baseData =
    filtered ? products : (Array.isArray(products) && products.length > 0 ? products : items);

  const totalCount = Array.isArray(baseData) ? baseData.length : 0;
  const indexOfLastItem = page * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = Array.isArray(baseData)
    ? baseData.slice(indexOfFirstItem, indexOfLastItem)
    : [];

  const paginate = (pageNumber) => {
    setSearchParams({
      ...(category ? { category } : {}),
      ...(keyword ? { keyword } : {}),
      page: pageNumber,
      ...(filtered ? { filtered: "1" } : {}),
    });
  };

  const showNotFound = !loading && totalCount === 0;

  return (
    <div className={styles.pageRoot}>
      <Navbar />

      <Container fluid className={styles.container}>
        <h1 className={styles.sectionTitle}>All Products</h1>

        <Row className="g-2 g-md-3">
          <Col xs={12} md={3} lg={2} className="d-none d-md-flex">
            <FilterBox applyFilter={applyFilter} onResetFilters={onResetFilters} />
          </Col>

          <Col xs={12} md={9} lg={10}>
            <ProductLayout category={category}>
              {loading ? (
                <Loader />
              ) : showNotFound ? (
                <div className={styles.notFound}>
                  <h2 className={styles.notFoundTitle}>No products found</h2>
                  {!!keyword && (
                    <p className={styles.notFoundHelp}>
                      We couldn't find results for <strong>{keyword}</strong>.
                    </p>
                  )}
                  <div className={styles.notFoundActions}>
                    <a
                      href="https://wa.link/9j108q"
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.whatsBtn}
                    >
                      Chat on WhatsApp
                    </a>
                    <button className={styles.clearBtn} onClick={onResetFilters}>
                      Clear filters
                    </button>
                  </div>
                </div>
              ) : (
                <Row className="g-2 g-md-3">
                  {currentItems.map((item) => (
                    <Col
                      key={item.id}
                      xs={6}
                      sm={6}
                      md={4}
                      lg={3}
                      xl={2}
                      className="d-flex"
                    >
                      <ProductCard item={item} />
                    </Col>
                  ))}
                </Row>
              )}
            </ProductLayout>

            {totalCount > 0 && (
              <div className={styles.paginationWrap}>
                <Pagination
                  currentPage={page}
                  totalPages={Math.ceil(totalCount / itemsPerPage)}
                  paginate={paginate}
                />
              </div>
            )}

            {error && <Alert variant="danger" className="mt-3 mb-0">{error}</Alert>}
          </Col>
        </Row>
      </Container>

      <Footer />
    </div>
  );
};

export default Product;
